import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { setModalId, usePublicState } from '@/store/slices/publicSlice';
import {
  setDisputeFilters,
  useDisputeFilters,
} from '@/store/slices/reportFiltersSlice';
import CustomModal from '@/components/atoms/CustomModal';
import InfoText from '@/components/atoms/InfoText';
import FormField from '@/components/FormField/FormField';
import Button from '@/components/atoms/Button';
import {
  useGetOrderMethodLKQuery,
  useGetDisputeRequesterRolesLKQuery,
} from '@/store/services/reports';
import DatesFilters from '../SharedComponents/DatesFilters';
import useDefaultReportDates from '../useDefaultReportDates';
import { useSharedFunctions } from '../SharedComponents/useSharedFunctions';

interface FormValues {
  method?: string[];
  requesterRole?: string[];
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

export default function FilterModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modalId } = usePublicState();
  const filters = useDisputeFilters();
  const { defaultFromDate, defaultToDate } = useDefaultReportDates();
  const { resetPage } = useSharedFunctions();

  const { data: orderMethod, isLoading: isStatusLoading } =
    useGetOrderMethodLKQuery();
  const { data: requesterRoles, isLoading: isRolesLoading } =
    useGetDisputeRequesterRolesLKQuery();

  const schema = z
    .object({
      method: z.array(z.string()).optional(),
      requesterRole: z.array(z.string()).optional(),
      fromDate: z.custom<Dayjs>().nullable(),
      toDate: z.custom<Dayjs>().nullable(),
    })
    .superRefine(({ fromDate, toDate }, ctx) => {
      if (fromDate && toDate && toDate.isBefore(fromDate, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('dispute.dateToBeforeFrom'),
          path: ['toDate'],
        });
      }
    });

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    values: {
      method: Array.isArray(filters.method)
        ? filters.method
        : filters.method
          ? [filters.method]
          : [],
      requesterRole: Array.isArray(filters.requesterRole)
        ? filters.requesterRole
        : filters.requesterRole
          ? [filters.requesterRole]
          : [],
      fromDate: filters.fromDate ? dayjs(filters.fromDate) : null,
      toDate: filters.toDate ? dayjs(filters.toDate) : null,
    },
  });

  const onClose = () => dispatch(setModalId(null));

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    !!watchAll.method?.length,
    !!watchAll.requesterRole?.length,
    watchAll.fromDate,
    watchAll.toDate,
  ].filter(Boolean).length;

  const onReset = () => {
    methods.reset({
      method: [],
      requesterRole: [],
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    });
  };

  const onSubmit = (values: FormValues) => {
    const fromDateStr = values.fromDate
      ? values.fromDate.format('YYYY-MM-DD')
      : '';
    const toDateStr = values.toDate ? values.toDate.format('YYYY-MM-DD') : '';

    dispatch(
      setDisputeFilters({
        method: values.method || [],
        requesterRole: values.requesterRole || [],
        fromDate: fromDateStr,
        toDate: toDateStr,
      })
    );

    resetPage();
    onClose();
  };

  const methodList = Array.isArray(orderMethod)
    ? orderMethod
    : (orderMethod as any)?.payload || [];

  const methodOptions =
    methodList?.map((method: any) => ({
      label: method.name,
      value: method.id,
    })) || [];

  const roleList = Array.isArray(requesterRoles)
    ? requesterRoles
    : (requesterRoles as any)?.payload || [];

  const roleOptions =
    roleList?.map((role: any) => ({
      label: role.name,
      value: role.id,
    })) || [];

  return (
    <CustomModal
      title={t('home.filters')}
      isOpen={modalId === 'DISPUTES_REPORT_MODAL'}
      onClose={onClose}
      subTitle={
        <div className="flex items-center gap-2">
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied')}`}
          />
          <button
            type="button"
            onClick={onReset}
            className="text-light-success hover:underline text-sm font-semibold"
          >
            {t('home.resetFilter')}
          </button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          {/* Requester Role Section */}
          <FormField
            name="requesterRole"
            id="requesterRole"
            type="checkBox"
            label={t('reports.requesterRole', 'Requester role')}
            options={roleOptions}
            disabled={isRolesLoading}
            className="grid grid-cols-2"
          />

          <hr className="border-t border-[#F0F0F0]" />

          {/* Method Section */}

          <FormField
            name="method"
            id="method"
            type="select"
            label={t('dispute.orderMethod', 'Order method')}
            placeholder={t('home.selectMethod', 'Select method')}
            options={methodOptions}
            loading={isStatusLoading}
            mode="multiple"
            allowClear
          />

          <hr className="border-t border-[#F0F0F0]" />

          {/* Date Range Section */}
          <DatesFilters />

          {/* Actions */}
          <div className="flex items-center justify-end gap-x-4 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[120px] h-[48px]"
            >
              {t('home.cancel')}
            </Button>
            <Button
              type="submit"
              variant="dark"
              className="min-w-[120px] h-[48px]"
            >
              {t('home.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
